import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'
import { Button } from '@olxbr/ds-core'

import BaseForm from '../BaseForm'
import PasswordInput from '../../molecules/PasswordInput'
import Subtitle from '../../atoms/Subtitle'
import StrongPasswordInput, { isValidPassword } from '../../molecules/StrongPasswordInput'
import { PASSWORD_MAX_LENGTH } from '../../../util/password'

const Content = styled(View)`
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
`

const SubtitleWrapper = styled(Subtitle)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const PageForm = styled.div`
  margin-top: 8px;
`

const PasswordWrapper = styled(View)`
  margin-top: 8px;
  margin-right: 0;
  margin-bottom: 8px;
  margin-left: 0;
`

const getPasswordExtraData = (key, password) => {
  const field = {}
  const passwordLevel = password.level

  if (passwordLevel === -1) {
    field[key] = 'sequential'
  } else if (passwordLevel === 1 || passwordLevel === 2) {
    field[key] = 'weak'
  } else if (passwordLevel === 3) {
    field[key] = 'good'
  } else if (passwordLevel === 4) {
    field[key] = 'strong'
  }
  return field
}

const passwordConfirmationMsg = 'As senhas estão diferentes. Tente novamente.'

class ChangePasswordBaseTemplate extends BaseForm {
  constructor(props) {
    super(props)
    const { showCurrentPasswodInput, showPasswodConfirmationInput } = props
    this.state = {
      isSubmitting: false,
      validators: {
        currentPassword: {
          action: null,
          message: 'Este campo é obrigatório.',
        },
        password: {
          action: this.isValidPassword,
          message: this.getPasswordErrorMessage,
        },
        passwordConfirmation: {
          action: this.isValidPasswordConfirmation,
          message: passwordConfirmationMsg,
        },
      },
      fields: {
        currentPassword: {
          value: !showCurrentPasswodInput || '',
          pristine: true,
          valid: !showCurrentPasswodInput,
        },
        password: {
          value: '',
          pristine: true,
          valid: false,
          level: 0,
        },
        passwordConfirmation: {
          value: !showPasswodConfirmationInput || '',
          pristine: true,
          valid: !showPasswodConfirmationInput,
        },
      },
    }
  }

  onCurrentPasswordChange = event => {
    const { value } = event.target
    this.updateField('currentPassword', value, !!value, false)
  }

  onPasswordChange = (value, level, msg) => {
    const { fields } = this.state
    const { showPasswodConfirmationInput } = this.props
    const passwordConfirmation = fields.passwordConfirmation.value
    this.setState({
      fields: {
        ...fields,
        password: {
          value,
          valid: this.isValidPassword(value, level),
          level,
          msg,
          pristine: false,
        },
      },
    })

    setTimeout(() => {
      if (showPasswodConfirmationInput && passwordConfirmation) {
        this.updateField(
          'passwordConfirmation',
          passwordConfirmation,
          this.isValidPasswordConfirmation(passwordConfirmation),
          false,
          passwordConfirmationMsg
        )
      }
    }, 1)
  }

  onPasswordChangeVariant = event => {
    const { fields } = this.state
    const { showPasswodConfirmationInput } = this.props
    const passwordConfirmation = fields.passwordConfirmation.value
    this.setState({
      fields: {
        ...fields,
        password: {
          value: event.target.value,
          valid: isValidPassword(event.target.value),
          pristine: false,
        },
      },
    })

    setTimeout(() => {
      if (showPasswodConfirmationInput && passwordConfirmation) {
        this.updateField(
          'passwordConfirmation',
          passwordConfirmation,
          this.isValidPasswordConfirmation(passwordConfirmation),
          false,
          passwordConfirmationMsg
        )
      }
    }, 1)
  }

  onPasswordConfirmationChange = event => {
    const { value } = event.target
    const valid = this.isValidPasswordConfirmation(value)
    const message = valid ? null : passwordConfirmationMsg
    this.updateField('passwordConfirmation', value, valid, false, message)
  }

  isValidPasswordConfirmation = passwordConfirmation => {
    const { showPasswodConfirmationInput } = this.props
    const fields = { ...this.state.fields }
    return showPasswodConfirmationInput ? passwordConfirmation === fields.password.value : true
  }

  shouldDisableButton = fields =>
    Object.values(fields).reduce((acc, value) => acc || !value.valid || !value.value, false)

  renderTitle() {
    const { renderTitle } = this.props
    if (typeof renderTitle === 'string') {
      return <SubtitleWrapper>{renderTitle}</SubtitleWrapper>
    }
    return renderTitle()
  }

  renderPassword() {
    const { fields } = this.state
    const { password } = fields

    return (
      <StrongPasswordInput
        label="Nova senha"
        value={password.value}
        onChange={this.onPasswordChangeVariant}
        onBlur={this.onPasswordChangeVariant}
        maxLength={PASSWORD_MAX_LENGTH}
      />
    )
  }

  handleSubmit = event => {
    event.preventDefault()
    const { lurker, submitEventName, onSubmit } = this.props
    const { fields } = this.state
    const { currentPassword, password } = fields
    this.setState({ isSubmitting: true })
    lurker.clickEvt(submitEventName)
    this.validateBeforeSend().then(() => {
      this.checkFormIsValid()
        .then(() => {
          onSubmit({ currentPassword, password })
            .then(() => this.setState({ isSubmitting: false }))
            .catch(() => {
              this.setState({ isSubmitting: false })
            })
        })
        .catch(() => {
          if (typeof lurker === 'object') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'invalid_fields',
              ...this.getInvalidFields(),
              ...getPasswordExtraData('invalid_password_reason', this.state.fields.password),
            })
          }
          this.setState({ isSubmitting: false })
        })
    })
  }

  render() {
    const { fields, isSubmitting } = this.state
    const { currentPassword, passwordConfirmation } = fields
    const passwordConfirmationHasError =
      !passwordConfirmation.valid && !passwordConfirmation.pristine
    const currentPasswordHasError = !currentPassword.valid && !currentPassword.pristine
    const disabledButton = this.shouldDisableButton(fields) || isSubmitting
    const { renderError, showCurrentPasswodInput, showPasswodConfirmationInput, buttonText } =
      this.props
    return (
      <Content>
        {renderError()}

        {this.renderTitle()}

        <PageForm>
          {showCurrentPasswodInput && (
            <PasswordWrapper>
              <PasswordInput
                label="Senha atual"
                arialabel="Digite sua senha atual"
                hasError={currentPasswordHasError}
                errorMsg={currentPassword.msg || 'Campo obrigatório. Informe sua senha.'}
                onChange={this.onCurrentPasswordChange}
                onBlur={this.onCurrentPasswordChange}
                value={currentPassword.value}
                disabled={isSubmitting}
                blurOnSubmit={false}
                returnKeyType="next"
                onSubmitEditing={() => this.updateFocus('password')}
              />
            </PasswordWrapper>
          )}

          <PasswordWrapper>{this.renderPassword()}</PasswordWrapper>

          {showPasswodConfirmationInput && (
            <PasswordWrapper>
              <PasswordInput
                label="Confirme sua senha"
                arialabel="Digite novamente sua senha"
                errorMsg={passwordConfirmation.msg}
                hasError={passwordConfirmationHasError}
                onChange={this.onPasswordConfirmationChange}
                onBlur={this.onPasswordConfirmationChange}
                value={passwordConfirmation.value}
                disabled={isSubmitting}
                inputRef={x => {
                  this.inputs.passwordConfirmation = x
                }}
                onSubmitEditing={this.handleSubmit}
              />
            </PasswordWrapper>
          )}

          <Button
            className="w-full"
            size="medium"
            disabled={disabledButton}
            onClick={this.handleSubmit}
          >
            {buttonText}
          </Button>
        </PageForm>
      </Content>
    )
  }
}

ChangePasswordBaseTemplate.defaultProps = {
  buttonText: 'Enviar',
  showCurrentPasswodInput: false,
  showPasswodConfirmationInput: false,
  submitEventName: 'submit',
}

ChangePasswordBaseTemplate.propTypes = {
  buttonText: PropTypes.string,
  lurker: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onSubmit: PropTypes.func,
  renderError: PropTypes.func,
  renderTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  showCurrentPasswodInput: PropTypes.bool,
  showPasswodConfirmationInput: PropTypes.bool,
  submitEventName: PropTypes.string,
  abTests: PropTypes.string,
}

export default ChangePasswordBaseTemplate
